import { useState, type ReactNode } from 'react'
import { Header } from './Header'
import { Sidebar } from './Sidebar'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { UserBizRole } from '@/utils/user-role'

type MainLayoutMenuPage = 'home' | 'analysis' | 'templates' | 'cases' | 'myspace' | 'catalog' | 'report'

export interface Tab {
  id: string
  label: string
  menu: MainLayoutMenuPage | string
  closable: boolean
}

interface MainLayoutProps {
  children: ReactNode
  currentMenu: MainLayoutMenuPage | string
  onNavigate: (menu: string) => void
  showSidebar?: boolean
  version?: string
  currentUser?: string
  userBizRole?: UserBizRole
  tabs?: Tab[]
  activeTab?: string
  onSwitchTab?: (menu: string) => void
  onCloseTab?: (tabId: string) => void
}

export function MainLayout({
  children,
  currentMenu,
  onNavigate,
  showSidebar = true,
  version,
  currentUser,
  userBizRole,
  tabs = [],
  activeTab,
  onSwitchTab,
  onCloseTab,
}: MainLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header currentMenu={currentMenu} onNavigate={onNavigate} currentUser={currentUser} />
      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Tab 栏 */}
          {tabs.length > 0 && (
            <div className="h-9 border-b bg-white flex items-center px-2 gap-0.5 overflow-x-auto shrink-0">
              {tabs.map(tab => {
                const isActive = activeTab === tab.menu
                return (
                  <div
                    key={tab.id}
                    className={cn(
                      "flex items-center gap-1 px-3 h-7 rounded-md text-xs cursor-pointer border transition-all shrink-0",
                      isActive
                        ? "bg-blue-50 text-gcb-primaryDark border-gcb-primary/30 font-medium"
                        : "bg-gray-50 text-gray-600 border-transparent hover:bg-gray-100 hover:border-gray-200"
                    )}
                    onClick={() => onSwitchTab?.(tab.menu)}
                  >
                    <span>{tab.label}</span>
                    {tab.closable && (
                      <button
                        className="ml-0.5 w-4 h-4 flex items-center justify-center rounded hover:bg-gray-200 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation()
                          onCloseTab?.(tab.id)
                        }}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
          <div className="flex-1 flex overflow-hidden">
            {showSidebar && (
              <Sidebar
                currentMenu={currentMenu}
                onNavigate={onNavigate}
                collapsed={sidebarCollapsed}
                onToggle={() => setSidebarCollapsed(c => !c)}
                version={version}
                currentUser={currentUser}
                userBizRole={userBizRole}
              />
            )}
            <main className="flex-1 overflow-auto">{children}</main>
          </div>
        </div>
      </div>
    </div>
  )
}

